// LoginDialogs.jsx (modified: add "記住我的帳密" checkbox to remember phone/password)

import React, { useEffect, useState, useContext } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  DialogActions,
  DialogContentText,
  Box,
  Alert,
  Link,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import sendVerification from "../api/app/sendVerification";
import register from "../api/app/register";
import verifyAccount from "../api/app/verifyAccount";

import login from "../api/app/login";
import api from "../api/api";
import { AuthContext } from "../context/AuthContext";
import { USERCONTRACT } from "../data/USERCONTRACT";
import ResetPasswordDialog from "./ResetPasswordDialog";
import { useTheme } from "@mui/material/styles";

const LS_REMEMBER_KEY = "remember_login";
const LS_PHONE_KEY = "remember_phone";
const LS_PASSWORD_KEY = "remember_password";

const LoginDialogs = ({ openLogin, handleLoginClose, setToken }) => {
  const theme = useTheme();

  // ===== theme-based styles =====
  const textFieldSx = {
    "& .MuiInputLabel-root": { color: theme.palette.text.secondary },
    "& .MuiInputLabel-root.Mui-focused": { color: theme.palette.info.main },
    "& .MuiOutlinedInput-root": {
      color: theme.palette.text.primary,
      "& fieldset": { borderColor: theme.palette.info.main },
      "&:hover fieldset": { borderColor: theme.palette.info.main },
      "&.Mui-focused fieldset": { borderColor: theme.palette.info.main },
    },
  };

  const actionPrimaryBtnSx = {
    fontWeight: "bold",
    backgroundColor: theme.palette.info.main,
    color: theme.palette.text.selected,
    textTransform: "none",
    "&:hover": {
      backgroundColor: theme.palette.background.default,
    },
  };

  const actionOutlineBtnSx = {
    color: theme.palette.info.main,
    textTransform: "none",
    border: `1px solid ${theme.palette.info.main}`,
    "&:hover": {
      border: `1px solid ${theme.palette.info.main}`,
      backgroundColor: `${theme.palette.info.main}14`, // ~8% tint
    },
  };

  const paperProps = {
    sx: {
      backgroundColor: theme.palette.background.default,
      borderRadius: 2,
      minWidth: { xs: 320, sm: 420 },
    },
  };

  // ===== state =====
  const [openRegister, setOpenRegister] = useState(false);
  const [openVerification, setOpenVerification] = useState(false);
  const [openForgotPassword, setOpenForgotPassword] = useState(false);
  const [openUserContract, setOpenUserContract] = useState(false);
  const [openResetPassword, setOpenResetPassword] = useState(false);

  const userContext = useContext(AuthContext);

  /* Login */
  const [loginData, setLoginData] = useState({
    phone: "",
    password: "",
    email: "",
  });

  // Remember checkbox
  const [rememberLogin, setRememberLogin] = useState(false);

  /* Register */
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordNotMatch, setPasswordNotMatch] = useState(false);
  const [phoneError, setPhoneError] = useState(false);

  /* Verification */
  const [verificationCode, setVerificationCode] = useState("");

  /* Global */
  const [successMsg, setSuccessMsg] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  /* Password visibility */
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleForgotPasswordOpen = () => {
    setPhone("");
    setError("");
    setOpenForgotPassword(true);
  };
  const handleForgotPasswordClose = () => setOpenForgotPassword(false);

  const handleRegisterOpen = () => {
    setOpenRegister(true);
    setPassword("");
    setConfirmPassword("");
    setError("");
  };

  const handleRegisterClose = () => setOpenRegister(false);

  const handleVerificationOpen = () => setOpenVerification(true);
  const handleVerificationClose = () => {
    setOpenVerification(false);
    setVerificationCode("");
  };

  // ===== Load remembered login when dialog opens =====
  useEffect(() => {
    if (!openLogin) return;

    setError("");
    setSuccessMsg("");

    const remembered = localStorage.getItem(LS_REMEMBER_KEY) === "1";
    const savedPhone = localStorage.getItem(LS_PHONE_KEY) || "";
    const savedPassword = localStorage.getItem(LS_PASSWORD_KEY) || "";

    setRememberLogin(remembered);

    setLoginData({
      phone: remembered ? savedPhone : "",
      password: remembered ? savedPassword : "",
      email: "",
    });
  }, [openLogin]);

  useEffect(() => {
    setPasswordNotMatch(
      !(password === "" && confirmPassword === "") &&
      password !== confirmPassword
    );
  }, [password, confirmPassword]);

  useEffect(() => {
    setPhoneError(phone !== "" && phone.length !== 10);
  }, [phone]);

  // ===== Forgot password: send SMS for reset =====
  const handleSendResetPasswordClick = async () => {
    if (!phone) {
      setError("請輸入手機號碼。");
      return;
    }

    if (phoneError) {
      setError("手機號碼格式錯誤。");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const responseData = await sendVerification(phone);
      if (responseData !== null) {
        handleForgotPasswordClose();
        setOpenResetPassword(true);
        setSuccessMsg("發送成功，請確認您的簡訊。");
      } else {
        setError("發送失敗");
      }
    } catch (err) {
      console.error(err?.message || err);
      setError("發送失敗，請稍後再試。");
    } finally {
      setLoading(false);
    }
  };

  // ===== Register: register + sendVerification (409 視為未驗證，可重送驗證碼) =====
  const handleSendVerificationButtonClick = async () => {
    if (!phone || !password || !confirmPassword) {
      setError("請完整填寫手機、密碼與確認密碼。");
      return;
    }
    if (phoneError) {
      setError("手機號碼格式錯誤。");
      return;
    }
    if (passwordNotMatch) {
      setError("密碼不相同。");
      return;
    }

    setError("");
    setLoading(true);

    let isResendForPendingUser = false;

    try {
      await register(name, phone, password, email);
    } catch (err) {
      const status = err?.response?.status;

      if (status === 409) {
        isResendForPendingUser = true;
      } else {
        console.error(err);
        setError(err?.response?.data?.message ?? "註冊失敗，請稍後再試。");
        setLoading(false);
        return;
      }
    }

    try {
      const verifyRes = await sendVerification(phone);

      if (!verifyRes) {
        setError("驗證碼發送失敗，請稍後再試。");
        setLoading(false);
        return;
      }

      if (isResendForPendingUser) {
        setSuccessMsg("此手機號碼尚未完成驗證，已重新發送驗證碼。");
      } else {
        setSuccessMsg("驗證碼已發送，請查收簡訊。");
      }

      handleVerificationOpen();
      handleRegisterClose();
    } catch (verifyErr) {
      console.error(verifyErr);
      setError("驗證碼發送失敗，請稍後再試。");
      setLoading(false);
      return;
    }

    setLoading(false);
  };

  // ===== Verify: verifyAccount → 暫存 → 正式 =====
  const handleRegisterButtonClick = async () => {
    if (verificationCode.length !== 6) {
      setError("驗證碼長度不足");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const verifyRes = await verifyAccount(phone, verificationCode);

      if (!verifyRes) {
        setError("驗證失敗，請重新輸入驗證碼。");
        setLoading(false);
        return;
      }

      handleVerificationClose();
      setSuccessMsg("註冊完成，請進行登入。");
    } catch (err) {
      console.error(err);
      setError("驗證失敗，請稍後再試。");
    } finally {
      setLoading(false);
    }
  };

  function persistRememberedLogin(nextRemember, phoneVal, passwordVal) {
    if (nextRemember) {
      localStorage.setItem(LS_REMEMBER_KEY, "1");
      localStorage.setItem(LS_PHONE_KEY, phoneVal || "");
      localStorage.setItem(LS_PASSWORD_KEY, passwordVal || "");
    } else {
      localStorage.setItem(LS_REMEMBER_KEY, "0");
      localStorage.removeItem(LS_PHONE_KEY);
      localStorage.removeItem(LS_PASSWORD_KEY);
    }
  }

  // ===== Login =====
  const handleLoginButtonClick = async () => {
    if (!loginData.phone || !loginData.password) {
      setError("請輸入帳號與密碼。");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const responseData = await login(loginData.phone, loginData.password);

      if (!responseData || !responseData.token) {
        setError(responseData?.message ?? "登入失敗，請確認帳號或密碼。");
        setLoading(false);
        return;
      }

      // remember or clear saved credentials based on checkbox
      persistRememberedLogin(rememberLogin, loginData.phone, loginData.password);

      handleLoginClose();
      localStorage.setItem("token", responseData.token);
      api.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${responseData.token}`;
      userContext.setUser(responseData.user);
      setSuccessMsg("登入成功。");
      setToken(responseData.token);
    } catch (err) {
      console.error(err);
      setError("登入失敗，請重新確認帳號密碼。");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Login */}
      <Dialog open={openLogin} onClose={handleLoginClose} PaperProps={paperProps}>
        <DialogTitle sx={{ fontWeight: "bold", color: theme.palette.text.primary }}>
          登入
          <Button
            aria-label="close"
            onClick={handleLoginClose}
            sx={{
              position: "absolute",
              right: 6,
              top: 10,
              color: theme.palette.text.primary,
              minWidth: 0,
            }}
          >
            ×
          </Button>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            variant="outlined"
            margin="dense"
            id="username"
            label="帳號"
            value={loginData.phone}
            onChange={(e) =>
              setLoginData((prev) => ({ ...prev, phone: e.target.value }))
            }
            type="text"
            fullWidth
            sx={textFieldSx}
          />
          <TextField
            margin="dense"
            id="password"
            label="密碼"
            value={loginData.password}
            onChange={(e) =>
              setLoginData((prev) => ({ ...prev, password: e.target.value }))
            }
            type={showLoginPassword ? "text" : "password"}
            fullWidth
            sx={textFieldSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowLoginPassword((prev) => !prev)}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showLoginPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Remember credentials */}
          <FormControlLabel
            sx={{ mt: 0.5, color: theme.palette.text.secondary }}
            control={
              <Checkbox
                checked={rememberLogin}
                onChange={(e) => {
                  const next = e.target.checked;
                  setRememberLogin(next);

                  // If user turns it off immediately, clear stored creds now
                  if (!next) {
                    persistRememberedLogin(false, "", "");
                  } else {
                    // If user turns it on, store current inputs immediately
                    persistRememberedLogin(true, loginData.phone, loginData.password);
                  }
                }}
              />
            }
            label="記住我的帳密"
          />

          {error && (
            <Alert
              icon={false}
              severity="warning"
              sx={{
                mt: 1,
                backgroundColor: "transparent",
                color: "red",
              }}
            >
              {error}
            </Alert>
          )}
          {successMsg && (
            <Alert
              severity="success"
              sx={{ mt: 1 }}
              onClose={() => setSuccessMsg("")}
            >
              {successMsg}
            </Alert>
          )}

          <Link
            sx={{ color: theme.palette.info.main, cursor: "pointer" }}
            onClick={handleForgotPasswordOpen}
          >
            忘記密碼
          </Link>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setError("");
              setOpenUserContract(true);
              handleLoginClose();
            }}
            sx={actionOutlineBtnSx}
          >
            建立會員
          </Button>
          <Box sx={{ flexGrow: 2 }} />
          <Button
            variant="contained"
            sx={{ ...actionPrimaryBtnSx, flexGrow: 1 }}
            disabled={loading}
            onClick={handleLoginButtonClick}
          >
            {loading ? "登入中..." : "登入"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Contract */}
      <Dialog
        open={openUserContract}
        onClose={() => setOpenUserContract(false)}
        PaperProps={paperProps}
      >
        <DialogTitle sx={{ color: theme.palette.text.primary }}>
          會員服務條款
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: theme.palette.text.primary }}>
            <Typography
              variant="body1"
              component="pre"
              sx={{ whiteSpace: "pre-wrap", color: theme.palette.text.primary }}
            >
              {USERCONTRACT}
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Button
            variant="outlined"
            onClick={() => setOpenUserContract(false)}
            sx={actionOutlineBtnSx}
          >
            不同意
          </Button>
          <Button
            variant="contained"
            sx={actionPrimaryBtnSx}
            onClick={() => {
              setOpenUserContract(false);
              handleRegisterOpen();
            }}
            autoFocus
          >
            同意
          </Button>
        </DialogActions>
      </Dialog>

      {/* Register */}
      <Dialog open={openRegister} onClose={handleRegisterClose} PaperProps={paperProps}>
        <DialogTitle sx={{ fontWeight: "bold", color: theme.palette.text.primary }}>
          註冊
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="phone"
            label="手機號碼"
            type="number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={phoneError}
            helperText={phoneError ? "手機號碼格式錯誤" : ""}
            fullWidth
            sx={textFieldSx}
          />
          <Box>
            <TextField
              margin="dense"
              id="name"
              label="姓名"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              sx={textFieldSx}
            />
          </Box>
          <TextField
            margin="dense"
            id="email"
            label="信箱"
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            sx={textFieldSx}
          />
          <TextField
            margin="dense"
            id="password"
            label="密碼"
            type={showRegisterPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            sx={textFieldSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle register password visibility"
                    onClick={() => setShowRegisterPassword((prev) => !prev)}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showRegisterPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="dense"
            id="confirmPassword"
            label="確認密碼"
            type={showRegisterPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={passwordNotMatch}
            helperText={passwordNotMatch ? "密碼不相同" : ""}
            fullWidth
            sx={textFieldSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle register password visibility"
                    onClick={() => setShowRegisterPassword((prev) => !prev)}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showRegisterPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {error && (
            <Alert
              icon={false}
              severity="warning"
              sx={{
                mt: 1,
                backgroundColor: "transparent",
                color: "red",
              }}
            >
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRegisterClose} sx={actionOutlineBtnSx}>
            取消
          </Button>
          <Box sx={{ flexGrow: 2 }} />
          <Button
            variant="contained"
            sx={{ ...actionPrimaryBtnSx, flexGrow: 1 }}
            onClick={handleSendVerificationButtonClick}
            disabled={loading}
          >
            {loading ? "註冊中..." : "註冊"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verification */}
      <Dialog
        open={openVerification}
        onClose={handleVerificationClose}
        PaperProps={paperProps}
      >
        <DialogTitle sx={{ fontWeight: "bold", color: theme.palette.text.primary }}>
          驗證
        </DialogTitle>
        <DialogContent>
          <TextField
            disabled
            margin="dense"
            id="phonetext"
            type="text"
            value={phone}
            fullWidth
            sx={textFieldSx}
          />
          <TextField
            margin="dense"
            id="verificationCode"
            label="請輸入驗證碼"
            type="number"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            fullWidth
            sx={textFieldSx}
          />
          {error && (
            <Alert
              icon={false}
              severity="warning"
              sx={{
                mt: 1,
                backgroundColor: "transparent",
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.info.main}`,
              }}
            >
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleVerificationClose} sx={actionOutlineBtnSx}>
            取消
          </Button>
          <Box sx={{ flexGrow: 2 }} />
          <Button
            variant="contained"
            sx={{ ...actionPrimaryBtnSx, flexGrow: 1 }}
            onClick={handleRegisterButtonClick}
            disabled={loading}
          >
            {loading ? "驗證中..." : "驗證"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Forgot Password */}
      <Dialog
        open={openForgotPassword}
        onClose={handleForgotPasswordClose}
        PaperProps={paperProps}
      >
        <DialogTitle sx={{ fontWeight: "bold", color: theme.palette.text.primary }}>
          忘記密碼
        </DialogTitle>
        <DialogContent>
          <Typography mb={1} sx={{ color: theme.palette.text.primary }}>
            發送簡訊來重新設定密碼。
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            id="phone"
            label="手機號碼"
            type="number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={phoneError}
            helperText={phoneError ? "手機號碼格式錯誤" : ""}
            fullWidth
            sx={textFieldSx}
          />
          {error && (
            <Alert
              icon={false}
              severity="warning"
              sx={{
                mt: 1,
                backgroundColor: "transparent",
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.info.main}`,
              }}
            >
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleForgotPasswordClose} sx={actionOutlineBtnSx}>
            取消
          </Button>
          <Box sx={{ flexGrow: 2 }} />
          <Button
            variant="contained"
            sx={{ ...actionPrimaryBtnSx, flexGrow: 1 }}
            onClick={handleSendResetPasswordClick}
            disabled={loading}
          >
            {loading ? "處理中..." : "送出"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password */}
      <ResetPasswordDialog
        open={openResetPassword}
        handleResetPasswordClose={() => setOpenResetPassword(false)}
        phone={phone}
      />
    </div>
  );
};

export default LoginDialogs;
